<template>
    <div class="modal-card card">
        <form @submit.prevent="handleSubmit">
            <div class="card-header">
                <!-- Title -->
                <h4 class="card-header-title">
                    Add Credential
                </h4>
                <!-- Close -->
                <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="card-body">
                <!-- Admin mode: the credential can be created for any aquacred service -->
                <template v-if="allowServiceSelect">
                    <InputField label="Service" class="mb-4" type="select" :options="serviceOptions"
                        v-model="service" :validate="requiredValidation('Service')" :toolTip="'Name of the service'"/>
                    <InputField v-if="usesSandboxNamespace" :label="`Choose a ${serviceConfig.namespaceLabel}`" :toolTip="'Sandbox under which the credential will be stored'"
                        class="mb-4" type="select" :options="sandboxOptions" v-model="namespace" :validate="requiredValidation('Sandbox')" />
                    <InputField v-else :label="serviceConfig.namespaceLabel" class="mb-4" type="text" :toolTip="namespaceToolTip"
                        v-model="namespace" :placeholder="`Enter a ${serviceConfig.namespaceLabel.toLowerCase()}`"
                        :editable="!namespaceDisabled" :validate="requiredValidation('Namespace')" />
                </template>
                <!-- Sandbox credentials page: service is always amdp, namespace is the sandbox -->
                <InputField v-else label="Choose a Sandbox" class="mb-4" type="select" :options="sandboxOptions"
                    v-model="namespace" />
                <hr class="mb-4">
                <h4>Credential Details</h4>
                <div class="me-3">
                    <FormFields :fields="credFields" v-model="credential" :editMode="true" :inlineField="true" />
                </div>
            </div>
            <div class="card-footer">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <div class="text-danger">
                            {{ error }}
                        </div>
                    </div>
                    <SubmitButton :loading="loading" :fields="credFields" v-model="credential"
                        :isDisabled="!service || !namespace" />
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import InputField from '../InputField.vue'
import FormFields from '../FormFields.vue'
import SubmitButton from '../SubmitButton.vue'
import sandboxAquacredCredentials from '../../utils/field-constants/sandboxAquacredCredentials'
import { AQUACRED_SERVICES, DEFAULT_AQUACRED_SERVICE, REQUIRED_CREDENTIAL_FIELDS, getServiceConfig } from '../../utils/aquacred'
import { mapActions } from 'vuex'
export default {
    components: { InputField, FormFields, SubmitButton },
    props: {
        modalData: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            loading: false,
            error: "",
            // `sandbox` is kept for the aMDP sandbox credentials page, `service`/`namespace`
            // are used by the admin credentials page
            service: this.modalData.service || DEFAULT_AQUACRED_SERVICE,
            namespace: this.modalData.namespace || this.modalData.sandbox || "",
            credential: {
                name: "",
                type: "aqfer"
            },
            serviceOptions: AQUACRED_SERVICES.map((s) => ({ label: s.label, value: s.value })),
            sandboxCredFields: JSON.parse(JSON.stringify(sandboxAquacredCredentials))
        }
    },
    name: "AddAquacredCredential",
    computed: {
        allowServiceSelect() {
            return !!this.modalData.allowServiceSelect;
        },
        serviceConfig() {
            return getServiceConfig(this.service);
        },
        usesSandboxNamespace() {
            return this.serviceConfig.namespaceType === 'sandbox';
        },
        // some services only accept their default namespace, so the input is locked
        namespaceDisabled() {
            return !!this.serviceConfig.disabled;
        },
        namespaceToolTip() {
            return this.namespaceDisabled
                ? `non-aMDP service credentials are only supported under the ${this.serviceConfig.namespaceDefault} namespace.`
                : 'Namespace under which the credential will be stored';
        },
        credFields() {
            // whether a field is required depends on the active type (e.g. `client_secret`
            // is required for papi but optional for google_ads) - REQUIRED_CREDENTIAL_FIELDS
            // is the single source of truth for that, so per-type required rules are derived
            // from it here rather than kept on each field's static `validation` array
            const requiredForType = REQUIRED_CREDENTIAL_FIELDS[this.credential.type] || [];
            let fields = this.sandboxCredFields.map((field) => {
                const nonRequiredRules = (field.validation || []).filter((rule) => rule.type !== 'required');
                const validation = requiredForType.includes(field.name)
                    ? [...nonRequiredRules, { type: 'required', errMsg: `${field.label} is required` }]
                    : nonRequiredRules;
                return {
                    ...field,
                    editable: true,
                    validation,
                    visible: field.filterByType.includes(this.credential.type)
                };
            });
            return fields;
        },
        sandboxOptions() {
            let devSandboxes = this.$store.state.sandbox.devSandboxes || [];
            let prodV2Sandboxes = this.$store.state.sandbox.prodV2Sandboxes || [];
            return [...devSandboxes, ...prodV2Sandboxes]
                .filter((sandbox) => sandbox.credential_manager === "aquacred")
                .map((sandbox) => {
                    return { label: sandbox.name + " - " + sandbox.env, value: sandbox.name };
                });
        },
    },
    watch: {
        service(value) {
            // the namespace means something different per service, so reset it
            const config = getServiceConfig(value);
            this.namespace = config.namespaceType === 'sandbox'
                ? (this.sandboxOptions.length ? this.sandboxOptions[0].value : "")
                : (config.namespaceDefault || "");
        },
    },
    methods: {
        ...mapActions('amdp', {
            insertAquacredCredential: 'upsertAquacredCredential'
        }),
        requiredValidation(fieldName) {
            return [
                { type: 'required', errMsg: `${fieldName} field is required` }
            ]
        },
        async handleSubmit() {
            this.error = "";
            this.loading = true;
            try {
                let payload = {
                    name: this.credential.name,
                    type: this.credential.type,
                }
                // add only visible fields to payload
                this.credFields.map((field) => {
                    if (field.visible && this.credential[field.name]) {
                        payload[field.name] = this.credential[field.name];
                    }
                });
                await this.insertAquacredCredential({
                    service: this.service,
                    namespace: this.namespace,
                    credentialName: this.credential.name,
                    payload: payload
                });
                this.$store.dispatch('addToast', {
                    message: "Credential added successfully.",
                    type: 'success'
                });
                this.$store.dispatch('hideModal');
                if (this.modalData?.refreshCredentials && typeof this.modalData.refreshCredentials === 'function') {
                    this.modalData.refreshCredentials({ service: this.service, namespace: this.namespace });
                }
            } catch (error) {
                this.error = error?.response?.data?.error || error?.message;
                console.error(this.error);
                this.$store.dispatch('addToast', {
                    message: "Something went wrong while updating credential. Please contact support.",
                    type: 'error'
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style></style>
